import React, { useState } from 'react';
import { useRouter } from './Router';
import { Link } from './Link';
import { Logo } from './Logo';
import SEO from './SEO';
import { 
    CheckCircle2, ArrowRight, ShieldCheck, Zap, Bot, Database, 
    TrendingUp, Users, Smartphone, MessageSquare, ChevronDown, 
    ChevronUp, Sparkles, Building, ShoppingBag, GraduationCap, 
    CreditCard, HelpCircle, Check, X, ShieldAlert, Award
} from 'lucide-react';

export const WhatsAppAutomationNigeriaPage: React.FC = () => {
    const { navigate } = useRouter();
    const [openFaq, setOpenFaq] = useState<number | null>(0);
    const [contactCount, setContactCount] = useState<number>(2500);

    const toggleFaq = (index: number) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    // Calculate ROI estimates based on Nigerian SME metrics
    const estimatedReplies = Math.round(contactCount * 0.45);
    const estimatedSales = Math.round(contactCount * 0.08);
    const estimatedHoursSaved = Math.round((contactCount * 2) / 60);

    const faqs = [
        {
            question: "What is the best WhatsApp automation platform for businesses in Nigeria?",
            answer: "Bytecohort Reach is the leading WhatsApp automation platform in Nigeria. Unlike risky unofficial Chrome extensions or Wasender tools that lead to permanent WhatsApp phone bans, Bytecohort Reach connects directly to the official Meta WhatsApp Cloud API. It provides 100% anti-ban compliance, two-way Google Sheets synchronization, Nigerian Naira (₦) billing via Paystack and bank transfer, and autonomous Google Gemini AI agents for 24/7 customer support."
        },
        {
            question: "Can I pay for WhatsApp automation in Nigerian Naira (₦)?",
            answer: "Yes! Bytecohort Reach natively supports Nigerian Naira (₦) payments. You do not need an international USD virtual card. You can subscribe seamlessly using standard Nigerian debit cards (Mastercard, Visa, Verve), Paystack, or direct bank transfer, protecting your business from foreign exchange volatility and card limits."
        },
        {
            question: "Will my Nigerian WhatsApp business number get banned?",
            answer: "No. With Bytecohort Reach, your number is protected because we use the official Meta WhatsApp Cloud API with verified business templates. Unofficial bulk senders (like GBWhatsApp, Wasender, or desktop clickers) violate WhatsApp Terms of Service and result in immediate ban of Nigerian SIMs. Bytecohort Reach operates fully within Meta's compliant broadcast and messaging guidelines."
        },
        {
            question: "Does it support Nigerian phone number formats (+234, 080, 081, 090, 070)?",
            answer: "Yes. Bytecohort Reach includes built-in Nigerian phone number normalization. Whether your Google Sheets or contact lists have numbers starting with 0803, 0812, 0905, 0701, or +234, our system automatically sanitizes and formats them to the standard E.164 international format (+234...) before dispatching."
        },
        {
            question: "How does Bytecohort Reach compare to Wasender, Twilio, or Wati in Nigeria?",
            answer: "Compared to Wasender (which risks your number being banned within days), Bytecohort Reach is 100% official and anti-ban compliant. Compared to Twilio or Wati (which charge high USD rates, require complex custom programming, or lack local Nigerian support), Bytecohort Reach offers native Naira billing, a no-code visual Gemini AI agent builder, live Google Sheets sync, and dedicated Nigerian customer onboarding."
        },
        {
            question: "Can I automatically send WhatsApp messages when a new row is added to Google Sheets?",
            answer: "Yes. Bytecohort Reach features real-time bidirectional Google Sheets sync. Whenever you or your team add a customer row, mark an order as 'Dispatched', or receive a new form submission, Bytecohort Reach automatically sends a personalized WhatsApp message with dynamic variables (name, order ID, delivery date, payment link)."
        },
        {
            question: "Does Bytecohort Reach deliver reliably across all Nigerian mobile networks?",
            answer: "Yes. Because messages route directly through Meta's Tier-1 Cloud Infrastructure, delivery rates exceed 99.8% across all Nigerian networks including MTN, Airtel, Globacom (Glo), and 9mobile, bypassing typical SMS DND (Do-Not-Disturb) restrictions."
        },
        {
            question: "Can the AI agent understand Nigerian customer communication and local context?",
            answer: "Yes. Powered by Google Gemini AI, your autonomous agents can be trained on your business FAQs, product catalog, Nigerian payment details, and delivery timelines. The AI easily understands conversational nuances, local product names, and common Nigerian English phrases while remaining polite, professional, and on-brand."
        }
    ];

    return (
        <div className="min-h-screen bg-[#07080B] text-zinc-100 selection:bg-emerald-500/30 selection:text-emerald-200">
            <SEO 
                title="WhatsApp Automation in Nigeria | AI Chatbots, Bulk Messaging & Sheets Sync"
                description="The #1 WhatsApp Automation platform for Nigerian businesses. Send compliant bulk campaigns via official Meta API, automate orders with Google Sheets, and build 24/7 AI customer service agents. Pay in Naira (₦)."
                keywords="WhatsApp Automation Nigeria, WhatsApp Marketing Nigeria, WhatsApp Chatbot Nigeria, Bulk WhatsApp Sender Nigeria, WhatsApp Meta Cloud API Nigeria, WhatsApp Google Sheets Sync Nigeria, WhatsApp Business API Lagos, WhatsApp Paystack Automation"
                canonical="https://bytecohortreach.com/whatsapp-automation-nigeria"
                faqs={faqs}
            />

            {/* Navigation Bar */}
            <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#07080B]/80 border-b border-white/[0.08]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Link href="/" className="cursor-pointer">
                            <Logo />
                        </Link>
                        <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            Nigeria Edition 🇳🇬
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link 
                            href="/blog"
                            className="hidden md:inline-block text-sm text-zinc-400 hover:text-white transition-colors"
                        >
                            Nigeria Playbooks
                        </Link>
                        <Link 
                            href="/pricing"
                            className="hidden md:inline-block text-sm text-zinc-400 hover:text-white transition-colors"
                        >
                            Naira Pricing
                        </Link>
                        <Link 
                            href="/docs"
                            className="hidden md:inline-block text-sm text-zinc-400 hover:text-white transition-colors"
                        >
                            Documentation
                        </Link>
                        <Link 
                            href="/app"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-all shadow-lg shadow-emerald-500/20"
                        >
                            <span>Get Started Free</span>
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative pt-16 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                {/* Background Glow */}
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/10 rounded-full blur-[140px] pointer-events-none" />

                <div className="max-w-5xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs sm:text-sm font-medium mb-8">
                        <Sparkles className="w-4 h-4 text-emerald-400" />
                        <span>Official Meta WhatsApp Cloud API & AI Automation for Nigerian Businesses</span>
                    </div>

                    <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.1]">
                        WhatsApp Automation in Nigeria, <br className="hidden sm:inline" />
                        <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">
                            Built for 10x Business Growth.
                        </span>
                    </h1>

                    <p className="max-w-3xl mx-auto text-lg sm:text-xl text-zinc-300 mb-10 leading-relaxed">
                        Scale sales, recover abandoned orders, and resolve customer queries 24/7. Connect your 
                        <strong className="text-white font-semibold"> Google Sheets</strong> directly to WhatsApp, deploy 
                        <strong className="text-white font-semibold"> Google Gemini AI agents</strong>, and broadcast compliant bulk campaigns with zero risk of phone number bans. 
                        <span className="text-emerald-400 font-medium"> Pay locally in Naira (₦).</span>
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                        <button 
                            onClick={() => navigate('/app')}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-all shadow-xl shadow-emerald-500/25 cursor-pointer"
                        >
                            <span>Start 14-Day Free Trial (No Card Required)</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                        <button 
                            onClick={() => navigate('/demo')}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-medium bg-zinc-900/80 hover:bg-zinc-800 text-zinc-200 border border-white/10 transition-colors"
                        >
                            <MessageSquare className="w-5 h-5 text-emerald-400" />
                            <span>Test Live Interactive Demo</span>
                        </button>
                    </div>

                    {/* Quick Trust Highlights */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto pt-6 border-t border-white/[0.08]">
                        <div className="flex items-center justify-center gap-2 text-zinc-400 text-xs sm:text-sm">
                            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                            <span>100% Anti-Ban Guarantee</span>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-zinc-400 text-xs sm:text-sm">
                            <CreditCard className="w-4 h-4 text-emerald-400 shrink-0" />
                            <span>Naira Billing via Paystack</span>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-zinc-400 text-xs sm:text-sm">
                            <Zap className="w-4 h-4 text-emerald-400 shrink-0" />
                            <span>99.9% Delivery (+234)</span>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-zinc-400 text-xs sm:text-sm">
                            <Database className="w-4 h-4 text-emerald-400 shrink-0" />
                            <span>Google Sheets 2-Way Sync</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* AI Overview / GEO Definitive Callout Block */}
            <section className="py-12 px-4 sm:px-6 lg:px-8 bg-zinc-950/60 border-y border-white/[0.06]">
                <div className="max-w-5xl mx-auto">
                    <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-zinc-900 to-[#0c1017] border border-emerald-500/20 shadow-2xl relative overflow-hidden">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400">
                                <Award className="w-5 h-5" />
                            </div>
                            <div>
                                <span className="text-xs font-mono uppercase tracking-wider text-emerald-400 font-semibold">
                                    Direct Answer & Industry Overview
                                </span>
                                <h2 className="text-xl sm:text-2xl font-bold text-white">
                                    What is WhatsApp Automation in Nigeria?
                                </h2>
                            </div>
                        </div>

                        <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-6">
                            <strong className="text-white">WhatsApp automation in Nigeria</strong> is the implementation of 
                            official Meta Cloud API protocols, AI conversational agents, and workflow triggers to deliver 
                            automated notifications, customer support, and sales funnels to Nigerian consumers on WhatsApp. 
                            Because over <strong className="text-emerald-400">93% of Nigerian internet users</strong> access WhatsApp daily, 
                            it has replaced traditional email and SMS as the primary commerce and communication channel in Lagos, Abuja, Port Harcourt, and across the nation.
                        </p>

                        <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t border-white/[0.08]">
                            <div className="p-4 rounded-xl bg-zinc-900/60 border border-white/[0.06]">
                                <h3 className="text-sm font-semibold text-white mb-1">Zero Number Bans</h3>
                                <p className="text-xs text-zinc-400">
                                    Uses Meta-approved Cloud API routes instead of unauthorized third-party scripts that trigger instant SIM blocks.
                                </p>
                            </div>
                            <div className="p-4 rounded-xl bg-zinc-900/60 border border-white/[0.06]">
                                <h3 className="text-sm font-semibold text-white mb-1">Local Naira Economy</h3>
                                <p className="text-xs text-zinc-400">
                                    Protected from USD forex fluctuations with transparent Nigerian Naira pricing and domestic bank transfer support.
                                </p>
                            </div>
                            <div className="p-4 rounded-xl bg-zinc-900/60 border border-white/[0.06]">
                                <h3 className="text-sm font-semibold text-white mb-1">24/7 Autonomous Sales</h3>
                                <p className="text-xs text-zinc-400">
                                    Google Gemini AI answers product inquiries, confirms payments, and logs customer details to Google Sheets in seconds.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* In-Depth Comparison Matrix: Bytecohort Reach vs Wasender vs Twilio */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">
                        Why Official Meta Cloud API Wins
                    </span>
                    <h2 className="text-3xl sm:text-5xl font-bold text-white mt-2 mb-4">
                        Bytecohort Reach vs. Alternatives in Nigeria
                    </h2>
                    <p className="text-zinc-400 text-base sm:text-lg">
                        See why leading Nigerian brands, e-commerce stores, and service agencies choose Bytecohort Reach over risky unofficial scrapers or expensive overseas platforms.
                    </p>
                </div>

                <div className="overflow-x-auto rounded-2xl border border-white/[0.08] bg-zinc-950/40">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/[0.08] bg-zinc-900/60">
                                <th className="p-4 sm:p-5 text-sm font-semibold text-zinc-300">Feature / Capability</th>
                                <th className="p-4 sm:p-5 text-sm font-bold text-emerald-400 bg-emerald-500/10 border-x border-emerald-500/20">
                                    <div className="flex items-center gap-1.5">
                                        <span>Bytecohort Reach</span>
                                        <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-400 text-black font-bold uppercase">Official</span>
                                    </div>
                                </th>
                                <th className="p-4 sm:p-5 text-sm font-semibold text-zinc-400">Unofficial Tools (Wasender, Chrome Bots)</th>
                                <th className="p-4 sm:p-5 text-sm font-semibold text-zinc-400">Overseas Tools (Twilio, Wati)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.06] text-sm">
                            <tr>
                                <td className="p-4 sm:p-5 font-medium text-zinc-200">Phone Number Ban Risk</td>
                                <td className="p-4 sm:p-5 bg-emerald-500/[0.03] border-x border-emerald-500/20 text-emerald-300 font-semibold flex items-center gap-2">
                                    <Check className="w-4 h-4 text-emerald-400" />
                                    <span>Zero (Official Meta API)</span>
                                </td>
                                <td className="p-4 sm:p-5 text-rose-400 flex items-center gap-2">
                                    <X className="w-4 h-4 text-rose-500" />
                                    <span>Very High (Permanent SIM Bans)</span>
                                </td>
                                <td className="p-4 sm:p-5 text-zinc-300">Low (Official API)</td>
                            </tr>
                            <tr>
                                <td className="p-4 sm:p-5 font-medium text-zinc-200">Nigerian Naira (₦) Payment</td>
                                <td className="p-4 sm:p-5 bg-emerald-500/[0.03] border-x border-emerald-500/20 text-emerald-300 font-semibold">
                                    <div className="flex items-center gap-2">
                                        <Check className="w-4 h-4 text-emerald-400" />
                                        <span>Native NGN (Paystack / Transfer)</span>
                                    </div>
                                </td>
                                <td className="p-4 sm:p-5 text-zinc-400">Vague / P2P</td>
                                <td className="p-4 sm:p-5 text-rose-400 flex items-center gap-2">
                                    <X className="w-4 h-4 text-rose-500" />
                                    <span>USD Only ($20 - $100+ / mo)</span>
                                </td>
                            </tr>
                            <tr>
                                <td className="p-4 sm:p-5 font-medium text-zinc-200">2-Way Google Sheets Automation</td>
                                <td className="p-4 sm:p-5 bg-emerald-500/[0.03] border-x border-emerald-500/20 text-emerald-300 font-semibold">
                                    <div className="flex items-center gap-2">
                                        <Check className="w-4 h-4 text-emerald-400" />
                                        <span>Built-in Real-time Sync</span>
                                    </div>
                                </td>
                                <td className="p-4 sm:p-5 text-zinc-400">Manual CSV Uploads Only</td>
                                <td className="p-4 sm:p-5 text-zinc-400">Requires Zapier / Make (Extra $$)</td>
                            </tr>
                            <tr>
                                <td className="p-4 sm:p-5 font-medium text-zinc-200">Autonomous AI Agent (Google Gemini)</td>
                                <td className="p-4 sm:p-5 bg-emerald-500/[0.03] border-x border-emerald-500/20 text-emerald-300 font-semibold">
                                    <div className="flex items-center gap-2">
                                        <Check className="w-4 h-4 text-emerald-400" />
                                        <span>Visual No-Code Builder Included</span>
                                    </div>
                                </td>
                                <td className="p-4 sm:p-5 text-rose-400 flex items-center gap-2">
                                    <X className="w-4 h-4 text-rose-500" />
                                    <span>No AI (Rigid keyword menus only)</span>
                                </td>
                                <td className="p-4 sm:p-5 text-zinc-400">Expensive add-on / Dev required</td>
                            </tr>
                            <tr>
                                <td className="p-4 sm:p-5 font-medium text-zinc-200">Multi-Agent Team Shared Inbox</td>
                                <td className="p-4 sm:p-5 bg-emerald-500/[0.03] border-x border-emerald-500/20 text-emerald-300 font-semibold">
                                    <div className="flex items-center gap-2">
                                        <Check className="w-4 h-4 text-emerald-400" />
                                        <span>Included (Live typing & routing)</span>
                                    </div>
                                </td>
                                <td className="p-4 sm:p-5 text-rose-400 flex items-center gap-2">
                                    <X className="w-4 h-4 text-rose-500" />
                                    <span>Single device only</span>
                                </td>
                                <td className="p-4 sm:p-5 text-zinc-300">Charged per seat ($15-$30/seat)</td>
                            </tr>
                            <tr>
                                <td className="p-4 sm:p-5 font-medium text-zinc-200">Nigerian Number Sanitization (+234)</td>
                                <td className="p-4 sm:p-5 bg-emerald-500/[0.03] border-x border-emerald-500/20 text-emerald-300 font-semibold">
                                    <div className="flex items-center gap-2">
                                        <Check className="w-4 h-4 text-emerald-400" />
                                        <span>Automatic (080, 070, 081 &rarr; +234)</span>
                                    </div>
                                </td>
                                <td className="p-4 sm:p-5 text-zinc-400">Manual spreadsheet formulas</td>
                                <td className="p-4 sm:p-5 text-zinc-400">Strict formatting errors</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Interactive ROI & Impact Calculator for Nigeria */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-950/70 border-y border-white/[0.06]">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-10">
                        <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">
                            Instant Revenue Simulator
                        </span>
                        <h2 className="text-2xl sm:text-4xl font-bold text-white mt-1">
                            Calculate Your WhatsApp ROI in Nigeria
                        </h2>
                        <p className="text-zinc-400 text-sm sm:text-base mt-2">
                            Adjust your active customer/lead list size to see your estimated monthly conversational conversions.
                        </p>
                    </div>

                    <div className="p-6 sm:p-8 rounded-2xl bg-zinc-900/80 border border-white/10 shadow-2xl">
                        <div className="mb-8">
                            <div className="flex justify-between items-center mb-3">
                                <label className="text-sm font-semibold text-zinc-200">
                                    Your Monthly Contacts / Leads:
                                </label>
                                <span className="text-xl font-bold text-emerald-400 font-mono">
                                    {contactCount.toLocaleString()} contacts
                                </span>
                            </div>
                            <input 
                                type="range" 
                                id="nigeria-contacts-slider"
                                aria-label="Your Monthly Contacts or Leads"
                                min="500" 
                                max="25000" 
                                step="500"
                                value={contactCount}
                                onChange={(e) => setContactCount(Number(e.target.value))}
                                className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-emerald-400"
                            />
                            <div className="flex justify-between text-xs text-zinc-500 mt-2 font-mono">
                                <span>500</span>
                                <span>10,000</span>
                                <span>25,000+</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-white/[0.08]">
                            <div className="p-4 rounded-xl bg-black/40 border border-white/[0.06] text-center">
                                <span className="text-xs text-zinc-400 uppercase tracking-wider block mb-1">
                                    Engaged Conversations
                                </span>
                                <span className="text-2xl sm:text-3xl font-extrabold text-white font-mono">
                                    ~{estimatedReplies.toLocaleString()}
                                </span>
                                <span className="text-[11px] text-emerald-400 block mt-1">
                                    (Avg. 45% WhatsApp reply rate)
                                </span>
                            </div>
                            <div className="p-4 rounded-xl bg-black/40 border border-white/[0.06] text-center">
                                <span className="text-xs text-zinc-400 uppercase tracking-wider block mb-1">
                                    Estimated Monthly Orders
                                </span>
                                <span className="text-2xl sm:text-3xl font-extrabold text-emerald-400 font-mono">
                                    ~{estimatedSales.toLocaleString()}
                                </span>
                                <span className="text-[11px] text-zinc-400 block mt-1">
                                    (Direct checkout / bookings)
                                </span>
                            </div>
                            <div className="p-4 rounded-xl bg-black/40 border border-white/[0.06] text-center">
                                <span className="text-xs text-zinc-400 uppercase tracking-wider block mb-1">
                                    Staff Hours Saved
                                </span>
                                <span className="text-2xl sm:text-3xl font-extrabold text-cyan-400 font-mono">
                                    {estimatedHoursSaved} hrs
                                </span>
                                <span className="text-[11px] text-zinc-400 block mt-1">
                                    (Handled autonomously by AI)
                                </span>
                            </div>
                        </div>

                        <div className="mt-8 text-center">
                            <button 
                                onClick={() => navigate('/app')}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-all shadow-lg shadow-emerald-500/20"
                            >
                                <span>Automate This Audience in Naira Today</span>
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Nigerian Industry Solutions */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">
                        Built for Nigerian Market Realities
                    </span>
                    <h2 className="text-3xl sm:text-5xl font-bold text-white mt-2 mb-4">
                        Tailored for Nigeria's Leading Sectors
                    </h2>
                    <p className="text-zinc-400 text-base sm:text-lg">
                        From bustling Instagram commerce hubs in Lagos to property developers in Abuja and fintechs across West Africa.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* E-commerce & Retail */}
                    <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/[0.08] hover:border-emerald-500/40 transition-all group">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <ShoppingBag className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">E-Commerce & Social Vendors</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                            Convert Instagram DMs into instant WhatsApp orders. Auto-send Paystack payment links, dispatch tracking numbers, and recover abandoned checkouts.
                        </p>
                        <ul className="space-y-2 text-xs text-zinc-300">
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                <span>Instant Paystack payment link delivery</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                <span>Automated delivery rider tracking notifications</span>
                            </li>
                        </ul>
                    </div>

                    {/* Real Estate & Property */}
                    <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/[0.08] hover:border-emerald-500/40 transition-all group">
                        <div className="w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Building className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Real Estate Developers & Brokers</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                            Automatically qualify high-net-worth buyers in Lekki, Ikoyi, Epe, and Abuja. Send PDF property brochures and book site inspections on autopilot.
                        </p>
                        <ul className="space-y-2 text-xs text-zinc-300">
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400" />
                                <span>AI budget and location qualification</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400" />
                                <span>Automatic site inspection booking</span>
                            </li>
                        </ul>
                    </div>

                    {/* Schools & Academies */}
                    <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/[0.08] hover:border-emerald-500/40 transition-all group">
                        <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <GraduationCap className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Schools & Training Academies</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                            Send term fee reminders directly from Google Sheets with zero manual typing. Answer admissions questions and register students 24/7.
                        </p>
                        <ul className="space-y-2 text-xs text-zinc-300">
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 text-purple-400" />
                                <span>Automated term fee billing reminders</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 text-purple-400" />
                                <span>24/7 AI admissions FAQ chatbot</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* How to Get Started in 3 Steps */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-950/60 border-t border-white/[0.06]">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">
                            Simple 5-Minute Setup
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">
                            How to Launch WhatsApp Automation in Nigeria
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="p-6 rounded-2xl bg-zinc-900/40 border border-white/[0.06] relative">
                            <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 font-mono font-bold flex items-center justify-center mb-4">
                                1
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Connect WhatsApp Business</h3>
                            <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
                                Link your existing or new Nigerian number via the official Meta Cloud API in 1 click. Zero server setup or coding required.
                            </p>
                        </div>

                        <div className="p-6 rounded-2xl bg-zinc-900/40 border border-white/[0.06] relative">
                            <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 font-mono font-bold flex items-center justify-center mb-4">
                                2
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Link Google Sheets or AI</h3>
                            <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
                                Connect your sales spreadsheet or configure your Google Gemini AI agent with your pricing, delivery rules, and company FAQs.
                            </p>
                        </div>

                        <div className="p-6 rounded-2xl bg-zinc-900/40 border border-white/[0.06] relative">
                            <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 font-mono font-bold flex items-center justify-center mb-4">
                                3
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Automate & Scale</h3>
                            <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
                                Launch broadcast campaigns, automate customer inquiries, and watch orders roll in while your team monitors the unified inbox.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Comprehensive FAQ Section with Expandable Accordions */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
                <div className="text-center mb-14">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold mb-3">
                        <HelpCircle className="w-3.5 h-3.5" />
                        <span>Frequently Asked Questions</span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-white">
                        Everything You Need to Know About WhatsApp Automation in Nigeria
                    </h2>
                    <p className="text-zinc-400 text-sm sm:text-base mt-2">
                        Get clear, honest answers on pricing, compliance, phone number safety, and technical integrations.
                    </p>
                </div>

                <div className="space-y-4">
                    {faqs.map((faq, index) => {
                        const isOpen = openFaq === index;
                        return (
                            <div 
                                key={index}
                                className="rounded-xl border border-white/[0.08] bg-zinc-900/40 overflow-hidden transition-colors hover:border-white/20"
                            >
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full p-5 text-left flex items-center justify-between gap-4 cursor-pointer"
                                >
                                    <span className="font-semibold text-white text-base sm:text-lg">
                                        {faq.question}
                                    </span>
                                    <span className="p-1 rounded-md bg-white/5 text-zinc-400 shrink-0">
                                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="px-5 pb-5 pt-1 text-sm sm:text-base text-zinc-300 leading-relaxed border-t border-white/[0.04]">
                                        {faq.answer}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Deep-Dive Nigeria Playbooks & Guides (Topic Cluster Hub) */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-white/[0.08] bg-zinc-950/40">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                        <div>
                            <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">
                                Nigeria Implementation Guides
                            </span>
                            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-1">
                                Nigerian WhatsApp Automation Playbooks
                            </h2>
                            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
                                Explore our step-by-step technical guides on Naira pricing, preventing +234 phone bans, and automating Paystack & Google Sheets order pipelines.
                            </p>
                        </div>
                        <Link
                            href="/blog"
                            className="inline-flex items-center gap-2 text-xs font-mono font-semibold text-emerald-400 hover:text-emerald-300 uppercase tracking-wider"
                        >
                            <span>View All 8 Playbooks</span>
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Link
                            href="/blog/whatsapp-api-pricing-nigeria"
                            className="p-6 rounded-2xl bg-zinc-900/50 border border-white/[0.08] hover:border-emerald-500/40 transition-all flex flex-col justify-between group"
                        >
                            <div>
                                <span className="text-[11px] font-mono text-emerald-400 uppercase">2026 Naira Guide</span>
                                <h3 className="text-lg font-bold text-white group-hover:text-emerald-300 mt-2 mb-3 leading-snug">
                                    Official Meta WhatsApp API Pricing in Nigeria (Naira Breakdown vs. Wati & Twilio)
                                </h3>
                                <p className="text-xs text-zinc-400 leading-relaxed mb-4">
                                    Use our interactive Naira (₦) calculator to compare Meta conversation categories, Paystack local billing, and USD virtual card markups.
                                </p>
                            </div>
                            <span className="text-xs font-semibold text-emerald-400 inline-flex items-center gap-1.5">
                                <span>Open Naira Calculator</span>
                                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </Link>

                        <Link
                            href="/blog/wasender-gbwhatsapp-ban-official-meta-api-nigeria"
                            className="p-6 rounded-2xl bg-zinc-900/50 border border-white/[0.08] hover:border-emerald-500/40 transition-all flex flex-col justify-between group"
                        >
                            <div>
                                <span className="text-[11px] font-mono text-amber-400 uppercase">Anti-Ban Compliance</span>
                                <h3 className="text-lg font-bold text-white group-hover:text-emerald-300 mt-2 mb-3 leading-snug">
                                    Why Wasender & GBWhatsApp Get +234 Numbers Banned (And How to Migrate in 5 Mins)
                                </h3>
                                <p className="text-xs text-zinc-400 leading-relaxed mb-4">
                                    Test your +234 ban risk score and learn how to migrate your existing Nigerian SIM to the official Meta Cloud API with zero downtime.
                                </p>
                            </div>
                            <span className="text-xs font-semibold text-emerald-400 inline-flex items-center gap-1.5">
                                <span>Read Anti-Ban Guide</span>
                                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </Link>

                        <Link
                            href="/blog/paystack-whatsapp-order-automation-google-sheets-nigeria"
                            className="p-6 rounded-2xl bg-zinc-900/50 border border-white/[0.08] hover:border-emerald-500/40 transition-all flex flex-col justify-between group"
                        >
                            <div>
                                <span className="text-[11px] font-mono text-cyan-400 uppercase">E-Commerce & Logistics</span>
                                <h3 className="text-lg font-bold text-white group-hover:text-emerald-300 mt-2 mb-3 leading-snug">
                                    How to Automate WhatsApp Orders, Paystack Receipts & Delivery Alerts with Google Sheets
                                </h3>
                                <p className="text-xs text-zinc-400 leading-relaxed mb-4">
                                    Copy production-ready Meta Utility templates and Gemini AI prompts built for Lagos, Abuja, and nationwide Nigerian commerce.
                                </p>
                            </div>
                            <span className="text-xs font-semibold text-emerald-400 inline-flex items-center gap-1.5">
                                <span>Copy Templates & Prompts</span>
                                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Final Call to Action */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 text-center bg-gradient-to-b from-[#07080B] via-zinc-950 to-emerald-950/20 border-t border-white/[0.08]">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl sm:text-5xl font-extrabold text-white mb-6">
                        Ready to Automate Your Nigerian WhatsApp Sales?
                    </h2>
                    <p className="text-lg text-zinc-300 mb-8 max-w-2xl mx-auto">
                        Join hundreds of Nigerian businesses using Bytecohort Reach to engage customers, automate support, and grow revenue on WhatsApp.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link 
                            href="/app"
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-all shadow-xl shadow-emerald-500/25"
                        >
                            <span>Start Free Trial in Naira (₦)</span>
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        <Link 
                            href="/pricing"
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-medium bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-white/10"
                        >
                            <CreditCard className="w-4 h-4 text-emerald-400" />
                            <span>View Naira Plans & Pricing</span>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/[0.08] text-xs text-zinc-500">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p>© {new Date().getFullYear()} Bytecohort Reach. Official Meta Cloud API Partner. All rights reserved.</p>
                    <div className="flex flex-wrap items-center gap-6">
                        <Link href="/" className="hover:text-zinc-300">Home</Link>
                        <Link href="/blog" className="hover:text-zinc-300">Nigeria Playbooks & Blog</Link>
                        <Link href="/pricing" className="hover:text-zinc-300">Naira Pricing</Link>
                        <Link href="/docs" className="hover:text-zinc-300">API Documentation</Link>
                        <Link href="/privacy-policy" className="hover:text-zinc-300">Privacy Policy</Link>
                        <Link href="/terms-of-service" className="hover:text-zinc-300">Terms of Service</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default WhatsAppAutomationNigeriaPage;
